import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { sendEmailVerification, signOut } from "firebase/auth";

export default function EmailVerificationPending({ user }) {
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });

    const handleResend = async () => {
        setSending(true);
        setMsg({ type: "", text: "" });
        try {
            await sendEmailVerification(user);
            setMsg({ type: "success", text: "Verification email resent! Check your inbox." });
        } catch (error) {
            console.error("Resend Error:", error);
            if (error.code === "auth/too-many-requests") {
                setMsg({ type: "error", text: "Too many requests. Please wait a bit." });
            } else {
                setMsg({ type: "error", text: "Failed to resend email." });
            }
        }
        setSending(false);
    };

    const handleLogout = () => {
        signOut(auth);
    };

    // Auto-reload check every few seconds to see if they verified in another tab
    useEffect(() => {
        const interval = setInterval(async () => {
            await user.reload();
            if (user.emailVerified) {
                window.location.reload();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [user]);

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-fade-in relative overflow-hidden">

                {/* Decorative Background Blob */}
                <div className="absolute top-[-20%] right-[-20%] w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-32 h-32 bg-purple-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

                <div className="relative z-10">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                        <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify your Email</h2>
                    <p className="text-gray-500 mb-6">
                        We've sent a verification link to <span className="font-semibold text-gray-700">{user.email}</span>.
                        <br className="hidden sm:block" /> Please check your inbox (and spam folder) to continue.
                    </p>

                    {msg.text && (
                        <div className={`text-sm p-3 rounded-lg mb-6 ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                            {msg.text}
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleResend}
                            disabled={sending}
                            className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
                        >
                            {sending ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Sending...
                                </>
                            ) : (
                                "Resend Verification Email"
                            )}
                        </button>

                        <button
                            onClick={handleLogout}
                            className="w-full bg-white text-gray-600 font-semibold py-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 hover:text-gray-800 transition-all"
                        >
                            Sign Out
                        </button>
                    </div>

                    <div className="mt-8 text-xs text-gray-400">
                        Wait for the email to arrive before clicking resend.
                    </div>
                </div>
            </div>
        </div>
    );
}
